import React, { Suspense, useCallback, useMemo, useState } from 'react'
import { gql, useQuery } from 'urql'
import { DateTime, Interval } from 'luxon'
import {
  Box,
  Button,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  CompareArrows,
  Edit,
  Delete,
  Person,
  PersonRemove,
  Today,
} from '@mui/icons-material'
import { Redirect } from 'wouter'
import AppLink from '../util/AppLink'
import { UserAvatar } from '../util/avatars'
import { QuerySetFavoriteButton } from '../util/QuerySetFavoriteButton'
import { GenericError, ObjectNotFound } from '../error-pages'
import { ScheduleDialog } from './EditDialogs'
import ScheduleDeleteDialog from '../schedules/ScheduleDeleteDialog'
import CoverDialog, { CoverMode } from './CoverDialog'
import ScheduleCalendarQuery from '../schedules/ScheduleCalendarQuery'
import TempSchedDialog from '../schedules/temp-sched/TempSchedDialog'
import TempSchedDeleteConfirmation from '../schedules/temp-sched/TempSchedDeleteConfirmation'
import {
  TempSchedValue,
  defaultTempSchedValue,
} from '../schedules/temp-sched/sharedUtils'
import {
  OverrideDialog,
  OverrideDialogContext,
} from '../schedules/ScheduleDetails'
import CalendarSubscribeButton from '../schedules/calendar-subscribe/CalendarSubscribeButton'
import { Page, Panel, Row, useBrand } from './ui'
import { theirTime, useTimezones, zoneName } from './tz'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }
const DAYS = 14

const query = gql`
  query ErecordsSchedule($id: ID!, $start: ISOTimestamp!, $end: ISOTimestamp!) {
    schedule(id: $id) {
      id
      name
      description
      timeZone
      shifts(start: $start, end: $end) {
        userID
        user {
          id
          name
        }
        start
        end
        truncated
      }
    }
  }
`

type DayPerson = {
  id: string
  name: string
  label: string
  start: string
  end: string
}

type Shift = {
  userID: string
  user: { id: string; name: string } | null
  start: string
  end: string
  truncated: boolean
}

// ScheduleDetail replaces upstream's calendar-first page: who is on call now,
// the next two weeks as a simple list, one-click cover/add, and the month
// calendar only when asked for.
export default function ScheduleDetail(props: {
  scheduleID: string
}): React.JSX.Element {
  const c = useBrand()
  const [range] = useState(() => {
    const now = DateTime.local()
    return {
      start: now.toISO() as string,
      end: now.plus({ days: DAYS }).toISO() as string,
    }
  })
  const [q] = useQuery({
    query,
    variables: { id: props.scheduleID, ...range },
    context: noSuspense,
    requestPolicy: 'cache-and-network',
  })
  const [edit, setEdit] = useState(false)
  const [del, setDel] = useState(false)
  const [showCal, setShowCal] = useState(false)
  const [override, setOverride] = useState<OverrideDialog | null>(null)
  const [tempSched, setTempSched] = useState<TempSchedValue | null>(null)
  const [tempEdit, setTempEdit] = useState(false)
  const [tempDelete, setTempDelete] = useState<TempSchedValue | null>(null)
  const [dayMenu, setDayMenu] = useState<{
    el: HTMLElement
    p: DayPerson
  } | null>(null)

  const zones = useTimezones()
  const s = q.data?.schedule
  const zone: string = s?.timeZone ?? 'local'
  const onNewTempSched = useCallback(() => {
    setTempEdit(false)
    setTempSched(defaultTempSchedValue(zone))
  }, [zone])
  const onEditTempSched = useCallback((v: TempSchedValue) => {
    setTempEdit(true)
    setTempSched(v)
  }, [])

  const shifts: Shift[] = s?.shifts ?? []
  const now = DateTime.local()
  const current = shifts.filter(
    (sh) => DateTime.fromISO(sh.start) <= now && DateTime.fromISO(sh.end) > now,
  )

  // the next DAYS days, each with who is on and when (in the schedule's zone)
  const days = useMemo(() => {
    const start = DateTime.local().setZone(zone).startOf('day')
    return Array.from({ length: DAYS }, (_, i) => {
      const d = start.plus({ days: i })
      const day = Interval.fromDateTimes(d, d.plus({ days: 1 }))
      const people = shifts
        .map((sh) => {
          const iv = Interval.fromDateTimes(
            DateTime.fromISO(sh.start),
            DateTime.fromISO(sh.end),
          )
          const part = iv.intersection(day)
          if (!part || part.isEmpty()) return null
          const allDay = part.length('hours') >= 23.9
          const label = allDay
            ? 'all day'
            : `${part.start?.setZone(zone).toFormat('HH:mm')}–${part.end?.setZone(zone).toFormat('HH:mm')}`
          return {
            id: sh.userID,
            name: sh.user?.name ?? 'Unknown',
            label,
            start: part.start?.toISO() as string,
            end: part.end?.toISO() as string,
          }
        })
        .filter(Boolean) as DayPerson[]
      return { d, people }
    })
  }, [shifts, zone])

  if (q.error) return <GenericError error={q.error.message} />
  if (!s) {
    if (del) return <Redirect to='/schedules' />
    return q.fetching ? (
      <Typography sx={{ p: 3, color: c.muted }}>Loading…</Typography>
    ) : (
      <ObjectNotFound />
    )
  }

  const cover = (sh?: Shift): void =>
    setOverride({
      variantOptions: ['replace'],
      removeUserReadOnly: Boolean(sh),
      defaultValue: {
        removeUserID: sh?.userID,
        start: DateTime.local().startOf('minute').toISO() as string,
        end: (sh
          ? DateTime.fromISO(sh.end)
          : DateTime.local().plus({ hours: 8 })
        ).toISO() as string,
      },
    })

  // the part of a day still ahead (overrides can't start in the past)
  const ahead = (
    startISO: string,
    endISO: string,
  ): { start: string; end: string } => {
    const st = DateTime.max(
      DateTime.fromISO(startISO),
      DateTime.local().startOf('minute'),
    )
    return { start: st.toISO() as string, end: endISO }
  }
  const dayOverride = (
    variant: 'add' | 'replace' | 'remove',
    win: { start: string; end: string },
    userID?: string,
  ): void => {
    setDayMenu(null)
    setOverride({
      variantOptions: [variant],
      removeUserReadOnly: Boolean(userID),
      defaultValue: { ...ahead(win.start, win.end), removeUserID: userID },
    })
  }

  return (
    <OverrideDialogContext.Provider
      value={{
        onNewTempSched,
        onEditTempSched,
        onDeleteTempSched: setTempDelete,
        setOverrideDialog: setOverride,
      }}
    >
      <Page
        title={s.name}
        subtitle={[s.description, `Times shown in ${zone}`]
          .filter(Boolean)
          .join(' · ')}
        maxWidth={1000}
        actions={
          <React.Fragment>
            <QuerySetFavoriteButton id={props.scheduleID} type='schedule' />
            <Button
              variant='outlined'
              startIcon={<Edit />}
              onClick={() => setEdit(true)}
            >
              Edit
            </Button>
          </React.Fragment>
        }
      >
        <Suspense>
          {edit && (
            <ScheduleDialog
              scheduleID={props.scheduleID}
              onClose={() => setEdit(false)}
            />
          )}
          {del && (
            <ScheduleDeleteDialog
              scheduleID={props.scheduleID}
              onClose={() => setDel(false)}
            />
          )}
          {override && (
            <CoverDialog
              scheduleID={props.scheduleID}
              zone={zone}
              modes={
                (
                  override.variantOptions ?? ['replace', 'add', 'remove']
                ).filter((v) => v !== 'temp') as CoverMode[]
              }
              removeUserID={override.defaultValue?.removeUserID}
              day={override.defaultValue?.start}
              onClose={() => setOverride(null)}
              onAdvanced={
                override.variantOptions?.includes('temp') ||
                !override.defaultValue
                  ? () => {
                      setOverride(null)
                      onNewTempSched()
                    }
                  : undefined
              }
            />
          )}
          {tempSched && (
            <TempSchedDialog
              value={tempSched}
              onClose={() => setTempSched(null)}
              scheduleID={props.scheduleID}
              edit={tempEdit}
            />
          )}
          {tempDelete && (
            <TempSchedDeleteConfirmation
              value={tempDelete}
              onClose={() => setTempDelete(null)}
              scheduleID={props.scheduleID}
            />
          )}
        </Suspense>

        <Stack spacing={2}>
          <Panel
            flush
            title='On call now'
            action={
              <Button
                size='small'
                startIcon={<Add />}
                onClick={() =>
                  setOverride({
                    variantOptions: ['add'],
                    removeUserReadOnly: false,
                  })
                }
              >
                Add someone
              </Button>
            }
          >
            {current.length === 0 && (
              <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
                Nobody is on call right now. Add someone, or set who takes turns
                below.
              </Typography>
            )}
            {current.map((sh) => (
              <Row
                key={sh.userID + sh.start}
                leading={
                  <UserAvatar
                    userID={sh.userID}
                    sx={{ width: 44, height: 44 }}
                  />
                }
                to={`/users/${sh.userID}`}
                title={sh.user?.name ?? 'Unknown'}
                subtitle={
                  sh.truncated
                    ? `On call for the next ${DAYS} days or more`
                    : `On call until ${DateTime.fromISO(sh.end).setZone(zone).toFormat('ccc d LLL, HH:mm')}`
                }
                trailing={
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<CompareArrows />}
                    onClick={() => cover(sh)}
                  >
                    Someone covers
                  </Button>
                }
              />
            ))}
          </Panel>

          <Panel
            flush
            title='Next two weeks'
            subtitle='Click a name to swap or remove someone for that day. Use Add to put another person on a day.'
            action={
              <Button
                size='small'
                variant='outlined'
                component={AppLink}
                to={`/schedules/${props.scheduleID}/assignments`}
              >
                Change regular schedule
              </Button>
            }
          >
            {days.map(({ d, people }) => {
              const today = d.hasSame(DateTime.local().setZone(zone), 'day')
              return (
                <Box
                  key={d.toISODate()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2.5,
                    py: 1,
                    '& + &': { borderTop: `1px solid ${c.line}` },
                    bgcolor: today ? c.hover : undefined,
                  }}
                >
                  <Box sx={{ width: 92, flexShrink: 0 }}>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 600, color: c.ink }}
                    >
                      {today ? 'Today' : d.toFormat('ccc')}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: c.muted }}>
                      {d.toFormat('d LLL')}
                    </Typography>
                  </Box>
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{ flexWrap: 'wrap', gap: 1, flex: 1 }}
                  >
                    {people.length === 0 && (
                      <Typography sx={{ fontSize: 13, color: 'warning.main' }}>
                        Nobody on call
                      </Typography>
                    )}
                    {people.map((p, i) => (
                      <Tooltip
                        key={p.id + i}
                        title={
                          zones[p.id] && zones[p.id] !== zone
                            ? `${p.name}'s time (${zoneName(zones[p.id])}): ${theirTime(p.start, p.end, zones[p.id])}`
                            : ''
                        }
                      >
                        <Chip
                          clickable
                          onClick={(ev) =>
                            setDayMenu({ el: ev.currentTarget, p })
                          }
                          avatar={<UserAvatar userID={p.id} />}
                          label={
                            <span>
                              {p.name}{' '}
                              <span style={{ color: c.muted }}>
                                · {p.label}
                              </span>
                            </span>
                          }
                          variant='outlined'
                          sx={{ ml: '0 !important' }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                  {DateTime.local() < d.plus({ days: 1 }) && (
                    <Button
                      size='small'
                      startIcon={<Add />}
                      onClick={() =>
                        dayOverride('add', {
                          start: d.toISO() as string,
                          end: d.plus({ days: 1 }).toISO() as string,
                        })
                      }
                    >
                      Add
                    </Button>
                  )}
                </Box>
              )
            })}
          </Panel>

          <Menu
            open={Boolean(dayMenu)}
            anchorEl={dayMenu?.el}
            onClose={() => setDayMenu(null)}
          >
            {dayMenu && [
              <MenuItem
                key='swap'
                onClick={() => dayOverride('replace', dayMenu.p, dayMenu.p.id)}
              >
                <ListItemIcon>
                  <CompareArrows fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary='Someone else covers'
                  secondary={`Instead of ${dayMenu.p.name}, ${dayMenu.p.label}`}
                />
              </MenuItem>,
              <MenuItem
                key='remove'
                onClick={() => dayOverride('remove', dayMenu.p, dayMenu.p.id)}
              >
                <ListItemIcon>
                  <PersonRemove fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary='Remove from this day'
                  secondary='Nobody replaces them'
                />
              </MenuItem>,
              <MenuItem
                key='profile'
                component={AppLink}
                to={`/users/${dayMenu.p.id}`}
              >
                <ListItemIcon>
                  <Person fontSize='small' />
                </ListItemIcon>
                <ListItemText primary='View profile' />
              </MenuItem>,
            ]}
          </Menu>

          <Panel flush title='Set up'>
            {[
              {
                to: 'assignments',
                title: 'Who takes turns',
                text: 'Rotations and people on this schedule, and when',
              },
              {
                to: 'overrides',
                title: 'Covers and changes',
                text: 'Temporary replacements, additions and removals',
              },
              {
                to: 'on-call-notifications',
                title: 'Announce on-call changes',
                text: 'Post to a Teams channel when the on-call person changes',
              },
              {
                to: 'escalation-policies',
                title: 'Used by',
                text: 'Escalation policies that page this schedule',
              },
              {
                to: 'shifts',
                title: 'Shift history',
                text: 'Past and future shifts as a list',
              },
            ].map((l) => (
              <Row
                key={l.to}
                to={`/schedules/${props.scheduleID}/${l.to}`}
                title={l.title}
                subtitle={l.text}
              />
            ))}
          </Panel>

          <Box>
            <Button startIcon={<Today />} onClick={() => setShowCal(!showCal)}>
              {showCal ? 'Hide month calendar' : 'Show month calendar'}
            </Button>
            {showCal && (
              <Box sx={{ mt: 1 }}>
                <ScheduleCalendarQuery scheduleID={props.scheduleID} />
              </Box>
            )}
          </Box>

          <Stack direction='row' spacing={1} sx={{ pt: 1 }}>
            <CalendarSubscribeButton scheduleID={props.scheduleID} />
            <Box sx={{ flex: 1 }} />
            <Button
              color='error'
              startIcon={<Delete />}
              onClick={() => setDel(true)}
            >
              Delete schedule
            </Button>
          </Stack>
        </Stack>
      </Page>
    </OverrideDialogContext.Provider>
  )
}
